/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import TQInitParams from "../../public/TQInitParam";
import { tqtypes, TQLogLevel } from "../../public/TQTypes";
import { FileSystem } from "../filesystem/TQFile";
import { LRUCache } from "./TQCache";

const kMaxDiskWriteFailCount = 5;

/* class DiskCache

	 	A class to manage the disk usage of fonts
	 	which is derived from template class 'LRUCache<key, value>'
	 	where
	 	key : postscript-name
	 	value: file-name stored on the disk
	 */
export class DiskCache extends LRUCache<string /*key*/, string /*value*/> {
    private mCacheDir: string;
    private mCurrentNumberOfFonts: number;
    private mMaxNumberOfFonts: number;
    private mDiskWriteFailCount: number;

    constructor(capacity: number, cacheDir: string, maxNumFontsToStore: number) {
        super(capacity);
        this.mCacheDir = cacheDir;
        this.mMaxNumberOfFonts = maxNumFontsToStore;
        this.mCurrentNumberOfFonts = 0;
        this.mDiskWriteFailCount = 0;
        TQInitParams.Logger.logMessage(TQLogLevel.TRACE, `DiskCache: Constructor called with capacity: ${capacity}.`);
        if (FileSystem.isOPFSEnabled()) {
            this.addExistingFilesToDiskCache(cacheDir);
        }
    }

    private async removeRequiredSpaceFromDisk(fileSize: number) {
        let status = true;
        while (status && (!this.haveSpace(fileSize) || this.mCurrentNumberOfFonts >= this.mMaxNumberOfFonts)) {
            // Remove the rear entry from cache to make space for new entry.
            const rearItem = this.getRear();
            if (rearItem != undefined) {
                // Delete the rear file from disk.
                status = await this.deleteFromDisk(rearItem.key, rearItem.value);

                // Decrease the currently stored fonts count
                if (status) this.mCurrentNumberOfFonts--;
            }
        }
    }

    private isFileTooBig(fontID: string, fileSize: number, maxSize: number) {
        if (fileSize > maxSize) {
            // Ignore such large file.
            TQInitParams.Logger.logMessage(TQLogLevel.WARN, `DiskCache: Failed adding fontID: ${fontID} of size ${fileSize}.`);
            return true;
        }
        return false;
    }

    private regularFileHandler(fontID: string, fileName: string, fileSize: number) {
        if (!this.find(fontID)) {
            this.mCurrentNumberOfFonts++;
        }
        const status: boolean = this.addToFront(fontID, fileName, fileSize);
        if (!status) {
            // Delete the font from disk and decrease the count.
            this.deleteFromDisk(fontID, fileName);
            this.mCurrentNumberOfFonts--;
        }
        return status;
    }

    private async addExistingFilesToDiskCache(cacheDir: string) {
        const results = await FileSystem.EnumerateDir(cacheDir);
        for (const newItem of results) {
            if (this.isFileTooBig(newItem.fileName, newItem.fileSize, this.maxCacheSize())) {
                continue;
            }
            await this.removeRequiredSpaceFromDisk(newItem.fileSize);
            // Handle regular file
            if (cacheDir == this.mCacheDir) {
                this.regularFileHandler(newItem.fileName, newItem.fileName, newItem.fileSize);
            }
        }
    }

    private async deleteFromDisk(key: string, fileName: string): Promise<boolean> {
        TQInitParams.Logger.logMessage(TQLogLevel.TRACE, `DiskCache::DeleteFromDisk deletion call for key ${key} and filename is ${fileName} !`);
        let success = false;

        const fileDeleted: boolean = await FileSystem.RemoveFile(this.mCacheDir, fileName);
        const isFileExistsPostDeletion: boolean = await FileSystem.FileExists(this.mCacheDir, fileName);

        if (fileDeleted || (!fileDeleted && !isFileExistsPostDeletion)) {
            this.remove(key);
            success = true;
        }
        return success;
    }

    // Returns a valid file-name if the file is available in cache(or disk).
    // else returns empty string.
    getFile(fontId: string): string {
        return this.get(fontId, "");
    }

    // Add a file of 'fileName' in cache and writes the 'fileData' of size 'fileSize' on the disk.
    // If the cache is full, then a least recently used file will be removed from cache and
    // will be deleted from the disk.
    async addFile(fontID: string, fileName: string, fileSize: number, fileData: Uint8Array): Promise<boolean> {
        const fileExists = await FileSystem.FileExists(this.mCacheDir, fileName);
        const lIsCached: boolean = this.find(fontID);
        let success = true;
        const cacheDir: string = this.mCacheDir;

        const size = this.maxCacheSize();

        const lHandleDiskWriteFailure = (fileName: string) => {
            this.mDiskWriteFailCount++;
            if (this.mDiskWriteFailCount > kMaxDiskWriteFailCount) {
                return new tqtypes.Error(tqtypes.ErrorCode.DISK_WRITE_FAILED, `DiskCache::AddFile - Writing file to disk failed! fileName: ${fileName}`, true);
            }
        };

        // No file already exists and nothing to write for marked override
        if (fileData == null && !fileExists) {
            // File was not found and there is no data, return error
            TQInitParams.Logger.logMessage(TQLogLevel.WARN, `DiskCache: File was not found and there is no data to write, return error : ${fontID} of size ${fileSize}.`);
            return false;
        }

        if (fileExists && lIsCached) {
            // file exists and is cached
            // Handle regular file
            if (success && cacheDir == this.mCacheDir) {
                return this.regularFileHandler(fontID, fileName, fileSize); //AddToFront take care of if found, moveToFront
            }
        } else if (fileExists) {
            // file exists on system but not cached
            if (this.isFileTooBig(fontID, fileSize, size)) {
                return false;
            }
            await this.removeRequiredSpaceFromDisk(fileSize);
            // Handle regular file
            if (success && cacheDir == this.mCacheDir) {
                return this.regularFileHandler(fontID, fileName, fileSize);
            }
        } // neither existed on filesystem nor cached
        else {
            // no meaning of override here
            if (this.isFileTooBig(fontID, fileSize, size)) {
                return false;
            }
            await this.removeRequiredSpaceFromDisk(fileSize);
            TQInitParams.Logger.logMessage(TQLogLevel.INFO, `DiskCache: Adding fontID in cache : ${fontID}`);
            success = await FileSystem.WriteFile(cacheDir, fileName, fileData, fileSize);
            if (!success) {
                lHandleDiskWriteFailure(fileName);
            }
            // Handle regular file
            if (success && cacheDir == this.mCacheDir) {
                return this.regularFileHandler(fontID, fileName, fileSize); //AddToFront take care of if found, moveToFront
            }
        }

        TQInitParams.Logger.logMessage(TQLogLevel.WARN, `DiskCache: Failed adding fontID : ${fontID} of size ${fileSize}.`);
        return false;
    }

    async removeFile(fontID: string, fileName: string): Promise<boolean> {
        const lCachePath = await FileSystem.FileExists(this.mCacheDir, fileName);
        if (lCachePath && this.find(fontID)) {
            this.deleteFromDisk(fontID, fileName);
            this.mCurrentNumberOfFonts--;
        }
        return true;
    }

    handleCacheSizeUpdate(newCacheSize: number): boolean {
        const diff: number = newCacheSize - this.maxCacheSize();
        this.addToMaxCapacity(diff);

        if (diff > 0) {
            // nothing to do here for now.
            return true;
        }

        this.updateApplicableCacheSize(newCacheSize);
        return true;
    }

    async updateApplicableCacheSize(newCacheSize: number): Promise<void> {
        let status = true;
        while (status && this.getCacheSize() > this.maxCacheSize()) {
            // Remove the rear entry from cache to make space for new entry.
            const rearItem = this.getRear();
            if (rearItem != undefined) {
                // Delete the rear file from disk.
                status = await this.deleteFromDisk(rearItem.key, rearItem.value);

                // Decrease the currently stored fonts count
                if (status) this.mCurrentNumberOfFonts--;
            }
        }
    }

    // Clears out the disk cache
    async clearDiskCache(): Promise<boolean> {
        let status = true;
        let rearItem = this.getRear();
        while (status && rearItem != undefined) {
            // Remove the rear entry from cache to make space for new entry.
            // Delete the rear file from disk.
            status = await this.deleteFromDisk(rearItem.key, rearItem.value);
            if (status) {
                this.mCurrentNumberOfFonts--;
            }
            rearItem = this.getRear();
        }
        return this.mCurrentNumberOfFonts == 0;
    }

    isNumberOfFontsFull(): boolean {
        return this.mCurrentNumberOfFonts >= this.mMaxNumberOfFonts;
    }

    updateMaxNumFonts(numFonts: number): void {
        this.mMaxNumberOfFonts = numFonts;
    }
}
